import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { generateSearchParam } from '@cms/utils/helpers'
import { IPagination } from '@dl/declarations'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { SystemTooltip } from '../ui/Tooltip'
import { Button } from './Button'
import { Dropdown } from './Dropdown'

interface PaginationProps {
    isLoading?: boolean
    pagination: IPagination
    total_count: number
    basePath: string;
}

export const PaginationV1: React.FC<PaginationProps> = (props) => {
    const { isLoading = false, pagination, total_count, basePath } = props
    const { skip, limit } = pagination

    const [, setSearchParams] = useSearchParams()

    const handlePageChange = (skip: number) => {
        setSearchParams((prevValue) => {
            prevValue.set(generateSearchParam(basePath, 'skip'), skip.toString());
            return prevValue;
        })
    }

    return (
        <div className="flex flex-col items-start justify-between gap-2 py-2 md:flex-row md:items-center md:justify-end md:py-4">
            <div>
                <p
                    className="text-sm"
                >
                    Showing {skip + 1} - {Math.min(skip + limit, total_count)} of {total_count}
                </p>
            </div>

            <div className='flex flex-col items-end sm:flex-row sm:items-center gap-2'>
                <Button
                    as='button'
                    onClick={() => handlePageChange(Math.max(0, skip - limit))}
                    inputVariant="gray_text_white_bg"
                    disabled={(skip === 0) || isLoading}
                    className="min-w-6 px-2"
                    shape="square"
                >
                    &lt;
                </Button>

                <Pages {...props} />

                <Button
                    as='button'
                    onClick={() => handlePageChange(skip + limit)}
                    inputVariant="gray_text_white_bg"
                    disabled={(skip + limit >= total_count) || isLoading}
                    className="min-w-6 px-2"
                    shape="square"
                >
                    &gt;
                </Button>
            </div>
        </div>
    )
}

interface PagesProps extends PaginationProps { }

const Pages: React.FC<PagesProps> = (props) => {
    const { isLoading = false, pagination, total_count, basePath } = props;
    const { skip, limit } = pagination;

    const [, setSearchParams] = useSearchParams();

    const handlePageChange = (skip: number) => {
        setSearchParams((prevValue) => {
            prevValue.set(generateSearchParam(basePath, 'skip'), skip.toString());
            return prevValue;
        })
    }

    const currentPage = Math.floor(skip / limit) + 1;
    const totalPages = Math.ceil(total_count / limit);
    const pages = [];

    // Show first page
    if (totalPages > 0) {
        pages.push(
            <Button
                key={1}
                as='button'
                inputVariant={currentPage === 1 ? "white_text_blue_bg" : "gray_text_white_bg"}
                disabled={isLoading}
                className="min-w-6 px-2"
                shape="square"
                // to={{
                //     search: '?skip=0'
                // }}
                onClick={() => handlePageChange(0)}
            >
                1
            </Button>
        );
    }

    // Show ellipsis if needed
    if (currentPage > 3) {
        pages.push(<span key="ellipsis1" className="px-2 text-gray-500">...</span>);
    }

    // Show pages around current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(
            <Button
                key={i}
                as='button'
                inputVariant={currentPage === i ? "white_text_blue_bg" : "gray_text_white_bg"}
                disabled={isLoading}
                className="min-w-6 px-2"
                shape="square"
                // to={{
                //     search: `?skip=${(i - 1) * limit}`
                // }}
                onClick={() => handlePageChange((i - 1) * limit)}
            >
                {i}
            </Button>
        );
    }

    // Show ellipsis if needed
    if (currentPage < totalPages - 2) {
        pages.push(<span key="ellipsis2" className="px-2 text-gray-500">...</span>);
    }

    // Show last page
    if (totalPages > 1) {
        pages.push(
            <Button
                key={totalPages}
                as='button'
                inputVariant={currentPage === totalPages ? "white_text_blue_bg" : "gray_text_white_bg"}
                disabled={isLoading}
                className="min-w-6 px-2"
                shape="square"
                // to={{
                //     search: `?skip=${(totalPages - 1) * limit}`
                // }}
                onClick={() => handlePageChange((totalPages - 1) * limit)}
            >
                {totalPages}
            </Button>
        );
    }

    return (
        <div className='flex items-center gap-2'>
            {pages}
        </div>
    );
}

export const PaginationV2: React.FC<PaginationProps> = (props) => {
    const { isLoading = false, pagination, total_count, basePath } = props
    const { skip = 0, limit } = pagination

    const [, setSearchParams] = useSearchParams()

    const currentPage = useMemo(() => Math.floor(skip / limit) + 1, [skip, limit]);
    const totalPages = useMemo(() => Math.ceil(total_count / limit), [total_count, limit]);

    const [wantedPage, setWantedPage] = useState(currentPage)

    const handlePageChange = (skip: number) => {
        setSearchParams((prevValue) => {
            prevValue.set(generateSearchParam(basePath, 'skip'), skip.toString());
            return prevValue;
        })
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            let pageNumber = wantedPage;
            if (pageNumber < 1) pageNumber = 1;
            if (pageNumber > totalPages) pageNumber = totalPages;
            handlePageChange((pageNumber - 1) * limit);
        }
    }

    useEffect(() => {
        setWantedPage(currentPage);
    }, [currentPage]);

    return (
        <div className="flex items-center justify-center gap-4">
            <Button
                as='button'
                showBorders={false}
                onClick={() => handlePageChange(Math.max(0, skip - limit))}
                inputVariant="gray_text_white_bg"
                disabled={(skip === 0) || isLoading}
                shape="square"
            >
                <MdKeyboardArrowLeft size={24} />
            </Button>

            <SystemTooltip
                message='Press Enter to go to the page'
            >
                <div className="flex items-center gap-2">

                    <input
                        type='number'
                        value={wantedPage}
                        onChange={(e) => setWantedPage(Number(e.target.value))}
                        className="w-10 h-10 border-2 border-cs-800 rounded-lg text-center"
                        min={1}
                        max={totalPages}
                        disabled={isLoading}
                        onKeyDown={handleKeyDown}
                    />

                    <span className="text-gray-600 text-lg">
                        of {totalPages}
                    </span>
                </div>
            </SystemTooltip>

            <Button
                as='button'
                showBorders={false}
                onClick={() => handlePageChange(skip + limit)}
                inputVariant="gray_text_white_bg"
                disabled={(skip + limit >= total_count) || isLoading}
                shape="square"
            >
                <MdKeyboardArrowRight size={24} />
            </Button>
        </div>
    )
}

export const PaginationV3: React.FC<PaginationProps> = (props) => {
    const { isLoading = false, pagination, total_count, basePath } = props
    const { skip = 0, limit } = pagination
    const [, setSearchParams] = useSearchParams()

    const currentPage = useMemo(() => Math.floor(skip / limit), [skip, limit]);
    const totalPages = useMemo(() => Math.ceil(total_count / limit), [total_count, limit]);

    const handlePageChange = (skip: number) => {
        setSearchParams((prevValue) => {
            prevValue.set(generateSearchParam(basePath, 'skip'), (skip * limit).toString());
            return prevValue;
        })
    }

    const skipOptions = useMemo(() => {
        return Array.from({ length: totalPages }, (_, i) => i + 1).map(i => ({
            _id: String(i),
            label: String(i),
            slug: String(i - 1),
            groupKey: 'Skip'
        }))
    }, [limit, totalPages])

    return (
        <div className="flex items-center justify-center gap-2">
            <SystemTooltip
                message='Current page'
            >
                <Dropdown
                    fixedOptions={totalPages > 1 ? [
                        {
                            _id: 'first-page',
                            label: 'First page',
                            slug: '0',
                            groupKey: 'Skip'
                        },
                        {
                            _id: 'last-page',
                            label: 'Last page',
                            slug: String(totalPages - 1),
                            groupKey: 'Skip'
                        }
                    ] : []}
                    options={skipOptions}
                    value={String(currentPage)}
                    handleChange={(option) => handlePageChange(Number(option.slug))}
                    searchOptions={{
                        allow: false
                    }}
                    labelWrapperClassName="border-0! shadow-none!"
                    inputSize='fit'
                    menuSize='fit'
                />
            </SystemTooltip>

            <button
                type='button'
                onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                disabled={(currentPage === 0) || isLoading}
                className='cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
            >
                <MdKeyboardArrowLeft size={28} />
            </button>

            <button
                type='button'
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= (totalPages - 1) || isLoading}
                className='cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
            >
                <MdKeyboardArrowRight size={28} />
            </button>
        </div>
    )
}
